// Angular-free maths for the label scanner's representative-image crop, kept
// apart from the page so a unit test can check it without a canvas — see
// label-scan-crop.test.ts.

import { ImageSize } from '@cf-shared/enums';

/** The AI's crop rectangle, as fractions (0..1) of the photo's width and height. */
export interface FractionalCropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A crop rectangle in whole photo pixels: where to start and how big to cut. */
export interface PixelCropRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

/**
 * The smallest a crop side may be, in pixels. The cropped picture is uploaded
 * with the multi-size ladder, which makes no size at all from a source thinner
 * than its smallest rung (256), and the food is then saved with no photo
 * (df-376e65c95f28, Sentry CYBERNETIC-FITNESS-APP-464). Small (480) rather than
 * Thumbnail (256): a 256 source yields only the square thumbnail, which for a
 * wide product strip keeps the middle and cuts off its ends, and 480 is the
 * minimum every other multi-size food-image upload already applies.
 *
 * The upload itself now also scales a picture smaller than 480 up
 * (df-5fbd3836e3a3), which covers a photo too small for this growth to help.
 * Growing the crop is still worth it: it keeps real pixels around the label
 * instead of blowing a thin strip up.
 */
export const MIN_LABEL_SCAN_CROP_EDGE: number = ImageSize.Small;

/**
 * Turn the AI's fractional crop into the pixel rectangle to cut from the photo.
 *
 * A side shorter than `MIN_LABEL_SCAN_CROP_EDGE` is grown around the crop's own
 * centre to that size, then shifted back inside the photo if it runs off an
 * edge. It is never grown past the photo itself, so on a photo smaller than the
 * minimum that side becomes the whole photo. A crop already big enough on both
 * sides comes out exactly as the plain rounding always gave.
 */
export function labelScanCropPixels(rect: FractionalCropRect, photoWidth: number, photoHeight: number): PixelCropRect {
  const [sx, sw] = growSpan(Math.round(rect.x * photoWidth), Math.round(rect.w * photoWidth), photoWidth);
  const [sy, sh] = growSpan(Math.round(rect.y * photoHeight), Math.round(rect.h * photoHeight), photoHeight);
  return { sx, sy, sw, sh };
}

/** One axis of the rule above: answers [start, length] in pixels. */
function growSpan(start: number, length: number, photoLength: number): [number, number] {
  // if this side is already long enough, keep it exactly as it was
  if (length >= MIN_LABEL_SCAN_CROP_EDGE) {
    return [start, length];
  }
  // grow to the minimum, but never past the photo itself
  const grownLength = Math.min(MIN_LABEL_SCAN_CROP_EDGE, photoLength);
  // keep the same centre, then clamp so the span stays inside the photo
  const centre = start + length / 2;
  const grownStart = Math.round(centre - grownLength / 2);
  const clampedStart = Math.max(0, Math.min(grownStart, photoLength - grownLength));
  return [clampedStart, grownLength];
}
